import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  User,
  Shield,
  Download,
  Trash2,
  AlertTriangle,
  Save,
  Bell,
  Mail
} from 'lucide-react';
import { formatDate } from '../utils/dateFormat';

export default function UserSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Notification preferences
  const [notificationPrefs, setNotificationPrefs] = useState({
    assessmentResults: true,
    bookingReminders: true,
    applicationUpdates: true,
    newResources: true,
    cohortUpdates: true,
    marketingEmails: false
  });
  const [savingPrefs, setSavingPrefs] = useState(false);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    linkedIn: '',
    company: ''
  });

  useEffect(() => {
    fetchProfile();
    fetchNotificationPrefs();
  }, []);

  const fetchNotificationPrefs = async () => {
    try {
      const response = await axios.get('/api/notifications/preferences');
      setNotificationPrefs(response.data.preferences);
    } catch (error) {
      console.error('Failed to fetch notification preferences:', error);
    }
  };

  const updateNotificationPref = async (key, value) => {
    const newPrefs = { ...notificationPrefs, [key]: value };
    setNotificationPrefs(newPrefs);
    
    try {
      setSavingPrefs(true);
      await axios.put('/api/notifications/preferences', { [key]: value });
      toast.success('Preference updated');
    } catch (error) {
      // Revert on error
      setNotificationPrefs(prev => ({ ...prev, [key]: !value }));
      toast.error('Failed to update preference');
    } finally {
      setSavingPrefs(false);
    }
  };

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/user/profile');
      setProfile(response.data.profile);
      setFormData({
        firstName: response.data.profile.firstName || '',
        lastName: response.data.profile.lastName || '',
        linkedIn: response.data.profile.linkedIn || '',
        company: response.data.profile.company || ''
      });
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      await axios.put('/api/user/profile', formData);
      toast.success('Profile updated successfully');
      fetchProfile();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleExportData = async () => {
    try {
      setExporting(true);
      const response = await axios.get('/api/user/data-export');
      
      // Create and download JSON file
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `path_data_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Data exported successfully');
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmEmail !== profile?.email) {
      toast.error('Email does not match. Please enter your exact email to confirm.');
      return;
    }
    
    try {
      setDeleting(true);
      await axios.post('/api/user/delete-account', {
        confirmEmail: deleteConfirmEmail,
        reason: 'User requested deletion'
      });
      toast.success('Account deleted successfully');
      // Redirect to home after deletion
      window.location.href = '/';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
      </div>
    );
  }

  return (
    <div data-testid="user-settings">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage your profile and preferences</p>
      </div>

      <div className="max-w-2xl">
        <div className="space-y-8">
          {/* Profile Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" /> Profile Information
              </CardTitle>
              <CardDescription>
                Update your personal information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Email</Label>
                <Input 
                  value={profile?.email || ''} 
                  disabled 
                  className="mt-1 bg-muted"
                />
                <p className="text-xs text-muted-foreground mt-1">Email cannot be changed</p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="mt-1"
                    placeholder="John"
                  />
                </div>
                <div>
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="mt-1"
                    placeholder="Doe"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="company">Company</Label>
                <Input
                  id="company"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="mt-1"
                  placeholder="Acme Inc."
                />
              </div>
              
              <div>
                <Label htmlFor="linkedIn">LinkedIn Profile URL</Label>
                <Input
                  id="linkedIn"
                  value={formData.linkedIn}
                  onChange={(e) => setFormData({ ...formData, linkedIn: e.target.value })}
                  className="mt-1"
                  placeholder="https://linkedin.com/in/yourprofile"
                />
              </div>
              
              <Button onClick={handleSaveProfile} disabled={saving}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </CardContent>
          </Card>

          {/* Notification Preferences */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5" /> Notification Preferences
              </CardTitle>
              <CardDescription>
                Control how and when you receive notifications
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* In-App & Email Notifications */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base">Assessment Results</Label>
                    <p className="text-sm text-muted-foreground">
                      Get notified when your assessment results are ready
                    </p>
                  </div>
                  <Switch
                    checked={notificationPrefs.assessmentResults}
                    onCheckedChange={(checked) => updateNotificationPref('assessmentResults', checked)}
                    data-testid="pref-assessment-results"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base">Booking Reminders</Label>
                    <p className="text-sm text-muted-foreground">
                      Receive reminders about upcoming sessions
                    </p>
                  </div>
                  <Switch
                    checked={notificationPrefs.bookingReminders}
                    onCheckedChange={(checked) => updateNotificationPref('bookingReminders', checked)}
                    data-testid="pref-booking-reminders"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base">Application Updates</Label>
                    <p className="text-sm text-muted-foreground">
                      Get notified when your application status changes
                    </p>
                  </div>
                  <Switch
                    checked={notificationPrefs.applicationUpdates}
                    onCheckedChange={(checked) => updateNotificationPref('applicationUpdates', checked)}
                    data-testid="pref-application-updates"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base">New Resources</Label>
                    <p className="text-sm text-muted-foreground">
                      Be notified when new content matching your interests is added
                    </p>
                  </div>
                  <Switch
                    checked={notificationPrefs.newResources}
                    onCheckedChange={(checked) => updateNotificationPref('newResources', checked)}
                    data-testid="pref-new-resources"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base">Cohort Updates</Label>
                    <p className="text-sm text-muted-foreground">
                      Receive updates about your cohort activities
                    </p>
                  </div>
                  <Switch
                    checked={notificationPrefs.cohortUpdates}
                    onCheckedChange={(checked) => updateNotificationPref('cohortUpdates', checked)}
                    data-testid="pref-cohort-updates"
                  />
                </div>
              </div>
              
              {/* Email Marketing */}
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base flex items-center gap-2">
                      <Mail className="w-4 h-4" /> Marketing Emails
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Receive promotional content and newsletters
                    </p>
                  </div>
                  <Switch
                    checked={notificationPrefs.marketingEmails}
                    onCheckedChange={(checked) => updateNotificationPref('marketingEmails', checked)}
                    data-testid="pref-marketing-emails"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Privacy & Data */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5" /> Privacy & Data
              </CardTitle>
              <CardDescription>
                Manage your data and privacy settings (GDPR compliant)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Data Export */}
              <div className="flex items-start justify-between p-4 bg-muted/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <Download className="w-5 h-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Download My Data</p>
                    <p className="text-sm text-muted-foreground">
                      Export all your data including assessments, results, and activity history as a JSON file.
                    </p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  onClick={handleExportData}
                  disabled={exporting}
                >
                  {exporting ? 'Exporting...' : 'Export'}
                </Button>
              </div>

              {/* Account Deletion */}
              <div className="flex items-start justify-between p-4 bg-destructive/5 border border-destructive/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <Trash2 className="w-5 h-5 text-destructive mt-0.5" />
                  <div>
                    <p className="font-medium text-destructive">Delete My Account</p>
                    <p className="text-sm text-muted-foreground">
                      Permanently delete your account and all associated data. This action cannot be undone.
                    </p>
                  </div>
                </div>
                <Button 
                  variant="destructive" 
                  onClick={() => setShowDeleteDialog(true)}
                >
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Account Info */}
          <Card>
            <CardHeader>
              <CardTitle>Account Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Account Created</span>
                <span>{profile?.createdAt ? formatDate(profile.createdAt) : '-'}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-muted-foreground">Account Type</span>
                <span className="capitalize">{profile?.role || 'User'}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" /> Delete Account
            </DialogTitle>
            <DialogDescription>
              This action is permanent and cannot be undone. All your data will be permanently deleted, including:
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
              <li>Your profile and account information</li>
              <li>All assessment results and history</li>
              <li>Conversation logs from assessments</li>
              <li>Booking and application records</li>
              <li>Email history and activity logs</li>
            </ul>
            
            <div className="mt-6">
              <Label>Type your email to confirm: <strong>{profile?.email}</strong></Label>
              <Input
                value={deleteConfirmEmail}
                onChange={(e) => setDeleteConfirmEmail(e.target.value)}
                placeholder="Enter your email"
                className="mt-2"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDeleteDialog(false);
              setDeleteConfirmEmail('');
            }}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteAccount}
              disabled={deleting || deleteConfirmEmail !== profile?.email}
            >
              {deleting ? 'Deleting...' : 'Permanently Delete Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
